import React, { useEffect, useRef } from 'react';
import {
  Box,
  VStack,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Icon,
  Text,
  Avatar,
  Spinner,
  Button,
} from '@chakra-ui/react';
import { FiSearch, FiAlertCircle, FiWifiOff } from 'react-icons/fi';
import { getShellBg, WarmShapes } from './scannerTheme';
import useManualStudentSearch from '../hooks/useManualStudentSearch';

// Búsqueda manual de alumno (mismo contrato público — active/onScan — que
// QRScanner.js/USBScanner.js, para que ScannerShell pueda intercambiarlos sin
// más cambios). En vez de decodificar un código, resuelve un token vía
// getOrCreateToken() para el alumno elegido y lo entrega por onScan() —
// reutiliza tal cual el pipeline de validate()/resolveStudentStatus() que ya
// usan cámara y USB, sin requerir ningún cambio de backend. La búsqueda en sí
// (debounce + getOrCreateToken) vive en useManualStudentSearch, compartida
// con ConsultaSearchBox (buscador inline del modo Consulta).
const ManualScanner = ({ active = true, onScan, isOnline = true, mode }) => {
  const { term, setTerm, results, searching, resolving, error, pick, retry } = useManualStudentSearch(active, onScan);
  const inputRef = useRef(null);

  // Foco automático al reactivarse — mismo patrón que QRScanner (lastScanRef)
  // / USBScanner (buffer), específico de esta pantalla (el buscador inline
  // de Consulta usa autoFocus nativo en el <Input> en vez de esto).
  useEffect(() => {
    if (active) {
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [active]);

  if (!active) {
    return (
      <Box position="absolute" inset={0} bg={getShellBg(mode)} display="flex" alignItems="center" justifyContent="center">
        <WarmShapes mode={mode} />
        <Text color="ink.700" fontSize="17px" fontWeight="600" position="relative" zIndex={1}>Búsqueda en pausa</Text>
      </Box>
    );
  }

  if (!isOnline) {
    return (
      <Box position="absolute" inset={0} bg={getShellBg(mode)} display="flex" alignItems="center" justifyContent="center" p={4}>
        <WarmShapes mode={mode} />
        <VStack spacing={3} position="relative" zIndex={1}>
          <Icon as={FiWifiOff} color="ink.400" boxSize={8} />
          <Text color="ink.700" textAlign="center" fontSize="16px" fontWeight="600">
            La búsqueda manual necesita conexión.<br />Usa cámara o USB mientras estés offline.
          </Text>
        </VStack>
      </Box>
    );
  }

  return (
    <Box position="absolute" inset={0} bg={getShellBg(mode)} overflowY="auto" px={4} py={6}>
      <WarmShapes mode={mode} />
      <VStack spacing={4} maxW="560px" mx="auto" pt={{ base: 6, md: 10 }} position="relative" zIndex={1}>
        <InputGroup size="lg">
          <InputLeftElement h="100%" pointerEvents="none"><Icon as={FiSearch} color="ink.400" /></InputLeftElement>
          <Input
            ref={inputRef} value={term} onChange={(e) => setTerm(e.target.value)}
            placeholder="Nombre, apellido o RUT…"
            bg="white" color="ink.900" border="1.5px solid" borderColor="cream.line2"
            borderRadius="14px" h="56px" fontSize="16px"
            boxShadow="0 2px 12px -4px rgba(35,30,26,.08)"
            _placeholder={{ color: 'ink.400' }}
            _focus={{ borderColor: 'coral.500', boxShadow: 'none' }}
            isDisabled={!!resolving}
          />
          {searching && <InputRightElement h="100%"><Spinner size="sm" color="coral.500" /></InputRightElement>}
        </InputGroup>

        {error && (
          <HStack bg="danger.bg" border="1px solid" borderColor="rgba(214,69,43,.35)"
            borderRadius="10px" px={3} py={2} w="full">
            <Icon as={FiAlertCircle} color="danger.500" boxSize={4} />
            <Text color="danger.500" fontSize="14px" fontWeight="600" flex={1}>{error}</Text>
            <Button size="sm" variant="ghost" color="danger.500" onClick={retry}>Reintentar</Button>
          </HStack>
        )}

        <VStack spacing={2} w="full" align="stretch">
          {results.map((u) => (
            <HStack key={u.id} as="button" type="button" onClick={() => pick(u)}
              bg="white" border="1px solid" borderColor="cream.line"
              _hover={{ bg: 'coral.tint', borderColor: 'coral.soft' }}
              borderRadius="12px" px={4} py={4} minH="64px" boxShadow="0 2px 10px -4px rgba(35,30,26,.08)"
              spacing={3} pointerEvents={resolving ? 'none' : 'auto'} opacity={resolving && resolving.id !== u.id ? 0.5 : 1}>
              <Avatar size="md" name={u.name} bg="coral.500" />
              <VStack align="flex-start" spacing={0} flex={1} minW={0}>
                <Text color="ink.900" fontWeight="700" fontSize="15.5px" noOfLines={1}>{u.name}</Text>
                <Text color="ink.400" fontSize="13.5px" noOfLines={1}>{u.username}{u.group ? ` · ${u.group}` : ''}</Text>
              </VStack>
              {resolving?.id === u.id && <Spinner size="sm" color="coral.500" />}
            </HStack>
          ))}
        </VStack>
      </VStack>
    </Box>
  );
};

export default ManualScanner;
